// Painted stage objects per biome (everything on the stage except the solid ground / wall tiles,
// which stay the procedural squares):
//   deck_*   one-way catwalk pieces (end, two middles, end), one per tile
//   haz_*    hazard strip pieces (emitter end, two middles, emitter end), one per tile
//   strut    catwalk support column (tiles vertically)
//   crate, terminal   prop textures
// Catwalks / hazards are drawn per tile by the room's Dressing over their collision tiles (catwalk
// tiles hidden, hazard cells drawn as plain ground underneath). Sources: tools/gen-parts.mjs
// tiles_<biome> + stage_<biome> → tools/stage-tiles.mjs / tools/stage-kit.mjs. Everything is
// optional: a biome without its art keeps the procedural objects.

import type Phaser from 'phaser';
import type { BiomeId } from '../../core/types';
import { BIOME_IDS } from '../../core/types';

export const STAGE_PIECES = [
  'deck_l', 'deck_m0', 'deck_m1', 'deck_r',
  'haz_l', 'haz_m0', 'haz_m1', 'haz_r',
  'strut', 'crate', 'terminal',
] as const;
export type StagePiece = (typeof STAGE_PIECES)[number];

export const stageKeys = {
  piece: (b: BiomeId, p: StagePiece) => `stage_${b}_${p}`,
  meta: (b: BiomeId) => `stage_${b}_tiles`,
  path: (b: BiomeId, file: string) => `assets/stage/${b}/${file}`,
};

export function queueStageAssets(scene: Phaser.Scene): void {
  for (const b of BIOME_IDS) {
    scene.load.json(stageKeys.meta(b), stageKeys.path(b, 'tiles.json'));
    for (const p of STAGE_PIECES) scene.load.image(stageKeys.piece(b, p), stageKeys.path(b, `${p}.webp`));
  }
}

export interface StageKit {
  /** Per-piece metadata: size, and `lift` = how far an end piece's top sits above the middles' top. */
  pieces: Record<string, { size: [number, number]; lift?: number }>;
  img: (p: StagePiece) => HTMLImageElement;
  key: (p: StagePiece) => string;
}

/** The biome's painted stage objects, or null when any piece failed to load. */
export function stageKit(scene: Phaser.Scene, biome: BiomeId): StageKit | null {
  const meta = scene.cache.json.get(stageKeys.meta(biome)) as { pieces?: StageKit['pieces'] } | undefined;
  if (!meta?.pieces || !STAGE_PIECES.every((p) => scene.textures.exists(stageKeys.piece(biome, p)))) return null;
  return {
    pieces: meta.pieces,
    key: (p) => stageKeys.piece(biome, p),
    img: (p) => scene.textures.get(stageKeys.piece(biome, p)).getSourceImage() as HTMLImageElement,
  };
}

/** Stable per-cell hash (variant picks). */
export function cellHash(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
